import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSectionsVisibility } from './hooks/useSectionsVisibility';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';
import Welcome from './pages/Welcome';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Statistics from './pages/Statistics';
import MyComments from './pages/MyComments';
import ProSpace from './pages/ProSpace';
// Explorer page removed — its content lives in /films (Discover) now.
import Messages from './pages/Messages';
import Films from './pages/Films';
import FilmIA from './pages/FilmIA';
import Studio from './pages/Studio';
import Admin from './pages/Admin';
import AdminAccounting from './pages/AdminAccounting';
import Moderation from './pages/Moderation';
import AdsValidation from './pages/AdsValidation';
import Analytics from './pages/Analytics';
import Trends from './pages/Trends';
import Reports from './pages/Reports';
import AdminGroupReports from './pages/AdminGroupReports';
import Notifications from './pages/Notifications';
import TikTokFeed from './components/TikTokFeed';
import ShopMarketplace from './pages/ShopMarketplace';
import ShopDetail from './pages/ShopDetail';
import ShopManage from './pages/ShopManage';
import ShopMyOrders from './pages/ShopMyOrders';
import AdminShops from './pages/AdminShops';
import AdminEsims from './pages/AdminEsims';
import AdminMyJob from './pages/AdminMyJob';
import AdminLegalDocs from './pages/AdminLegalDocs';
import AdminEDepots from './pages/AdminEDepots';
import EsimSuccessPage from './pages/EsimSuccessPage';
import MyMouvHome from './pages/MyMouvHome';
import MyMouvEventDetail from './pages/MyMouvEventDetail';
import MyJobList from './pages/MyJobList';
import MyJobDetail from './pages/MyJobDetail';
import MyMouvMyTickets from './pages/MyMouvMyTickets';
import MyMouvSuccess from './pages/MyMouvSuccess';
import MyMouvScanner from './pages/MyMouvScanner';
import SplashScreen from './components/SplashScreen';
import IncomingCallProvider from './components/IncomingCallProvider';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { initializeNativeFeatures, forceHideSplashScreen } from './capacitor-utils';
import { Capacitor } from '@capacitor/core';

// Backend URL configuration
// - On production web (app.destine.studio): use relative URLs (same domain)
// - On iOS/Android native app: use production backend
// - On preview/development: use REACT_APP_BACKEND_URL
const getBackendUrl = () => {
  // Check if running on native platform (iOS/Android)
  if (Capacitor.isNativePlatform()) {
    // Native apps should use production backend
    return 'https://app.destine.studio';
  }
  
  // Check if running on production web
  if (typeof window !== 'undefined' && window.location.hostname === 'app.destine.studio') {
    // Production web uses relative URLs (same domain)
    return '';
  }
  
  // Preview/development uses the env variable
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

if (BACKEND_URL) {
  axios.defaults.baseURL = BACKEND_URL;
}
axios.defaults.withCredentials = true;

// v50.11 — Premium gate: intercept 402 responses centrally so users get a
// consistent "cette fonctionnalité est Premium" message. The subscription
// modal is opened manually via the "Passez à Premium" button in Déesse.
let _premiumToastLast = 0;
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const detail = err.response.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message)
        ? detail.message
        : "Cette fonctionnalité est incluse dans l'abonnement Premium.";
      const now = Date.now();
      if (now - _premiumToastLast > 1500) {
        _premiumToastLast = now;
        toast.error(msg, { duration: 4500 });
      }
    }
    return Promise.reject(err);
  }
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-magenta-500 to-violet-900">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/" replace />;
};

// v50.10.9 — when the admin hides the Home tab, /feed redirects to Discover (keeps ?story_user=).
const HomeGate = ({ children }) => {
  const { homeHidden, loaded } = useSectionsVisibility();
  const location = useLocation();
  if (loaded && homeHidden) return <Navigate to={`/films${location.search || ''}`} replace />;
  return children;
};

function AppContent() {
  const [showSplash, setShowSplash] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    // Initialize Capacitor native features
    initializeNativeFeatures();
    
    // Fallback: Force hide native splash screen after 3 seconds max
    const fallbackTimer = setTimeout(() => {
      forceHideSplashScreen();
    }, 3000);
    
    return () => clearTimeout(fallbackTimer);
  }, []);

  // Heartbeat for online presence tracking
  useEffect(() => {
    if (!user) return;
    
    const sendHeartbeat = async () => {
      try {
        await axios.post('/api/users/heartbeat');
      } catch (e) {
        // Ignore heartbeat errors
      }
    };
    
    // Send immediately and then every 2 minutes
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 2 * 60 * 1000);
    
    return () => clearInterval(heartbeatInterval);
  }, [user]);

  if (showSplash) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Welcome />} />
          <Route path="/cgu" element={<Navigate to="https://info.destine.studio" replace />} />
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <HomeGate><TikTokFeed /></HomeGate>
              </ProtectedRoute>
            }
          />
          {/* Explorer tab removed — its sections moved into /films (Discover) */}
          <Route path="/explorer" element={<Navigate to="/films" replace />} />
          {/* Deep-link routes for shared content — podcasts now live in Home/Studio tab */}
          <Route path="/podcast/:id" element={<Navigate to="/feed?tab=studio" replace />} />
          <Route path="/explorer/article/:id" element={<Navigate to="/films" replace />} />
          {/* MyShop */}
          <Route path="/shop" element={<ProtectedRoute><ShopMarketplace /></ProtectedRoute>} />
          <Route path="/shop/manage" element={<ProtectedRoute><ShopManage /></ProtectedRoute>} />
          <Route path="/shop/my-orders" element={<ProtectedRoute><ShopMyOrders /></ProtectedRoute>} />
          <Route path="/shop/order/success" element={<ProtectedRoute><ShopMarketplace /></ProtectedRoute>} />
          <Route path="/shop/order/cancel" element={<ProtectedRoute><ShopMarketplace /></ProtectedRoute>} />
          <Route path="/shop/extra/success" element={<ProtectedRoute><ShopManage /></ProtectedRoute>} />
          <Route path="/shop/extra/cancel" element={<ProtectedRoute><ShopManage /></ProtectedRoute>} />
          <Route path="/shop/:id" element={<ProtectedRoute><ShopDetail /></ProtectedRoute>} />
          <Route path="/admin/shops" element={<ProtectedRoute><AdminShops /></ProtectedRoute>} />
          <Route path="/admin/esims" element={<ProtectedRoute><AdminEsims /></ProtectedRoute>} />
          <Route path="/admin/myjob" element={<ProtectedRoute><AdminMyJob /></ProtectedRoute>} />
          <Route path="/admin/legal-docs" element={<ProtectedRoute><AdminLegalDocs /></ProtectedRoute>} />
          <Route path="/admin/edepots" element={<ProtectedRoute><AdminEDepots /></ProtectedRoute>} />
          <Route path="/esim/success" element={<ProtectedRoute><EsimSuccessPage /></ProtectedRoute>} />
          <Route path="/esim/cancel" element={<Navigate to="/" replace />} />
          {/* MyMouv — Events & concerts */}
          <Route path="/mymouv" element={<ProtectedRoute><MyMouvHome /></ProtectedRoute>} />
          <Route path="/mymouv/events/:eventId" element={<MyMouvEventDetail />} />
          <Route path="/myjob" element={<MyJobList />} />
          <Route path="/myjob/:offerId" element={<MyJobDetail />} />
          <Route path="/mymouv/my-tickets" element={<ProtectedRoute><MyMouvMyTickets /></ProtectedRoute>} />
          <Route path="/mymouv/tickets/success" element={<ProtectedRoute><MyMouvSuccess /></ProtectedRoute>} />
          <Route path="/mymouv/scanner" element={<ProtectedRoute><MyMouvScanner /></ProtectedRoute>} />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/new/:receiverId"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:conversationId"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/films"
            element={
              <ProtectedRoute>
                <Films />
              </ProtectedRoute>
            }
          />
          <Route
            path="/film-ia"
            element={
              <ProtectedRoute>
                <FilmIA />
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio"
            element={
              <ProtectedRoute>
                <Studio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          {/* Username-based profile route used by @mentions */}
          <Route
            path="/profile/u/:username"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/my-comments"
            element={
              <ProtectedRoute>
                <MyComments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/statistics"
            element={
              <ProtectedRoute>
                <Statistics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pro-space"
            element={
              <ProtectedRoute>
                <ProSpace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute>
                <Moderation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ads-validation"
            element={
              <ProtectedRoute>
                <AdsValidation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trends"
            element={
              <ProtectedRoute>
                <Trends />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/group-reports"
            element={
              <ProtectedRoute>
                <AdminGroupReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounting"
            element={
              <ProtectedRoute>
                <AdminAccounting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />

        </Routes>
      </BrowserRouter>
      <IncomingCallProvider />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
